import { useEffect, useMemo, useState } from "react";
import studies from "../../../Data/studies.json";
import variablesData from "../../../Data/cdem_variables.json";
import {
  fetchAllSurveyData,
  fetchDemocracyCheckupData,
  isRequestAborted,
} from "../../../utils/api";
import { getDbSettings } from "../../../utils/dbConfig";
import { findStudyCategory } from "../../../utils/studies";
import {
  buildGroupsForPrefix,
  buildQuestionsIndexForPrefix,
  yearToPrefix,
} from "../utils/chartHelpers";

export const useChartData = (selectedFile) => {
  const [sqlData, setSqlData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dataError, setDataError] = useState(null);

  const category = useMemo(() => findStudyCategory(studies, selectedFile), [selectedFile]);

  const { dbName, configError } = useMemo(() => {
    if (!selectedFile || !category) {
      return { dbName: null, configError: null };
    }

    try {
      const settings = getDbSettings(category.datasetKey, {
        year: selectedFile.year,
        provinceKey: selectedFile.provinceKey,
      });
      return { dbName: settings?.dbName || null, configError: null };
    } catch (error) {
      return { dbName: null, configError: error };
    }
  }, [selectedFile, category]);

  useEffect(() => {
    const isDemocracyCheckup = category?.datasetKey === "democracy_checkup";

    if (!selectedFile) {
      setSqlData([]);
      setDataError(null);
      setIsLoading(false);
      return undefined;
    }

    if (configError) {
      setSqlData([]);
      setDataError("Selected study configuration is invalid.");
      setIsLoading(false);
      return undefined;
    }

    if (!isDemocracyCheckup && !dbName) {
      setSqlData([]);
      setDataError("Study data source is unavailable.");
      setIsLoading(false);
      return undefined;
    }

    let isMounted = true;
    const controller = new AbortController();

    const loadData = async () => {
      setIsLoading(true);
      setDataError(null);

      try {
        const rows = isDemocracyCheckup
          ? await fetchDemocracyCheckupData(selectedFile.year, {
              signal: controller.signal,
            })
          : await fetchAllSurveyData(dbName, { signal: controller.signal });

        if (!isMounted) return;
        setSqlData(Array.isArray(rows) ? rows : []);
      } catch (error) {
        if (!isMounted || isRequestAborted(error)) return;
        setSqlData([]);
        setDataError("Could not load study data. Please try again.");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [selectedFile, dbName, configError, category]);

  const studyPrefix = useMemo(
    () => (selectedFile ? yearToPrefix(selectedFile.year) : null),
    [selectedFile]
  );

  const questionsIndex = useMemo(
    () => (studyPrefix ? buildQuestionsIndexForPrefix(studyPrefix) : {}),
    [studyPrefix]
  );

  const indepGroups = useMemo(
    () => (studyPrefix ? buildGroupsForPrefix(variablesData?.Independent, studyPrefix) : []),
    [studyPrefix]
  );

  const depGroups = useMemo(
    () => (studyPrefix ? buildGroupsForPrefix(variablesData?.Dependent, studyPrefix) : []),
    [studyPrefix]
  );

  return {
    sqlData,
    isLoading,
    dataError,
    questionsIndex,
    indepGroups,
    depGroups,
  };
};
